import React, { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';

/**
 * Click-outside-aware dropdown for grouping secondary toolbar actions.
 * Usage:
 *   <ActionMenu>
 *     <ActionMenu.Item icon={<Mail/>} onClick={...}>Email</ActionMenu.Item>
 *   </ActionMenu>
 */
const ActionMenu = ({ children, label = 'More', testid = 'action-menu' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Provide a `closeMenu` function to children via React Context so each
  // Item can close the dropdown BEFORE running its own onClick handler.
  // Doing it in the bubble-up phase (the old pattern) unmounts the Item
  // button mid-click on iOS Safari, which sometimes drops the synthetic
  // click and leaves the user thinking the action did nothing.
  const closeMenu = () => setOpen(false);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Filter falsy children (so conditional `cond && <Item/>` works) and
  // inject the closeMenu callback into each Item. Callers often group items
  // in a fragment (`<>…</>`); flatten those first — cloning a fragment with
  // an extra prop is invalid and React warns about it.
  const flatten = (nodes) =>
    React.Children.toArray(nodes).filter(Boolean).flatMap((child) =>
      React.isValidElement(child) && child.type === React.Fragment
        ? flatten(child.props.children)
        : [child],
    );
  const items = flatten(children).map((child, i) =>
    React.isValidElement(child)
      ? React.cloneElement(child, { key: child.key ?? `item-${i}`, _closeMenu: closeMenu })
      : child,
  );
  if (items.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-2.5 sm:px-3 py-2 rounded-lg"
        data-testid={`${testid}-toggle`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal size={14} />
        <span className="hidden sm:inline">{label}</span>
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-30 py-1"
          role="menu"
          data-testid={`${testid}-panel`}
        >
          {items}
        </div>
      )}
    </div>
  );
};

const Item = ({ icon, onClick, disabled, children, testid, tone = 'default', _closeMenu }) => {
  const toneCls = {
    default: 'text-slate-700 hover:bg-slate-100',
    primary: 'text-teal-700 hover:bg-teal-50',
    danger: 'text-red-600 hover:bg-red-50',
    warning: 'text-amber-700 hover:bg-amber-50',
  }[tone] || 'text-slate-700 hover:bg-slate-100';
  // Run the user's onClick FIRST (so state updates queue), then close the
  // menu on a microtask. On iOS Safari this prevents the click from being
  // dropped when the Item button is unmounted as part of the close.
  const handleClick = (e) => {
    if (disabled) return;
    if (typeof onClick === 'function') {
      try { onClick(e); } catch (err) { console.error('[ActionMenu Item] onClick failed:', err); }
    }
    if (typeof _closeMenu === 'function') {
      // Defer the close to the next tick so React processes the consumer's
      // state update (e.g. setWarrantyOpen(true)) before this component
      // tree starts unmounting the button.
      setTimeout(_closeMenu, 0);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left disabled:opacity-50 disabled:cursor-not-allowed ${toneCls}`}
      role="menuitem"
      data-testid={testid}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span className="flex-1 truncate">{children}</span>
    </button>
  );
};

ActionMenu.Item = Item;
ActionMenu.Divider = () => <div className="my-1 border-t border-slate-100" />;

export default ActionMenu;
